import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";

export type RecipeState = "private" | "published";
export type RecipeLens = "released" | "latest";

export interface RecipePerson {
  name: string | null;
  is_me: boolean;
}

export interface NamedId {
  id: string;
  /** Null when the reader has no name to see for this id. */
  name: string | null;
}

/** One thing the person can pick: `value` goes back verbatim, `label` is read. */
export interface RecipeChoice {
  value: unknown;
  label: string;
}

/** Mirrors `server/src/api/recipe_reads.rs::ParamForm`. `default` and every
 *  choice `value` are already in the run-side shape; the form never converts. */
export interface RecipeParamForm {
  name: string;
  /** `PhysicalKind` variant name: `String`, `I64`, `Decimal`, `Date`, … */
  kind: string;
  params: {
    decimal: { precision: number; scale: number } | null;
    timestamp_tz: string | null;
  };
  required: boolean;
  default: unknown;
  choices: {
    source: "free" | "literal" | "categorical" | "distinct";
    options: RecipeChoice[];
  };
  bound_column: NamedId | null;
}

/** Mirrors `SlotForm`: `default` and every `allowed[].value` are complete
 *  `picks` entries (`{kind, …}`), sent back verbatim. */
export interface RecipeSlotForm {
  kind: "grain" | "dimension" | "optional_filter";
  name: string;
  default: unknown;
  allowed: RecipeChoice[];
}

export interface RecipeDetail {
  recipe_id: string;
  name: string;
  description: string | null;
  state: RecipeState;
  version: number;
  lens: RecipeLens;
  lens_choice: boolean;
  /** Inputs with no published cut. While any is listed the released lens is
   *  not on offer — a run under it would serve their latest rows. */
  unreleased_inputs: NamedId[];
  author: RecipePerson;
  created_at: string;
  authored_at: string;
  /** The DEFAULT combination — what the statement stores, because a slot is
   *  anchored by matching its default. */
  sql: string;
  /** The same definition as the family it is: swap sites carrying slot names,
   *  droppable conjuncts bracketed. Absent when the recipe has no slots. */
  sql_generic?: string;
  input_tables: NamedId[];
  params: RecipeParamForm[];
  slots: RecipeSlotForm[];
  cannot_run: {
    reason: string;
    params?: string[];
    dead_inputs?: RecipeDeadInput[];
    /** `would_not_plan` only: what the planner said, verbatim. */
    detail?: string;
  } | null;
  unlinked_joins: string[];
  /** The two acts only the author has (R26). Absent for everyone else. */
  manage?: RecipeManage;
}

/** Mirrors `server/src/api/recipe_reads.rs::Manage`. */
export interface RecipeManage {
  deprecated: boolean;
  /** `delete` for a private recipe, `deprecate` for a published one. Never both. */
  verb: "delete" | "deprecate";
  /** Results this recipe has made, across every model. All go with a delete. */
  results: number;
  /** Models among those the caller may not delete from. Counted, never named. */
  blocked_models: number;
}

/** One row of the catalog listing. */
export interface RecipeListItem {
  recipe_id: string;
  name: string;
  description: string | null;
  state: RecipeState;
  deprecated: boolean;
  mine: boolean;
  author: RecipePerson;
  created_at: string;
}

export interface RecipeListPage {
  recipes: RecipeListItem[];
  /** Absent on the last page, and on every search — a relevance ranking has
   *  nowhere for "the next 50 by name" to resume from. */
  next?: { name: string; recipe_id: string };
}

/** A column the recipe reads that is no longer in its table. `restorable` means
 *  it was deleted and can be brought back; otherwise it is gone for good and
 *  the definition has to change. */
export interface RecipeDeadInput {
  table: NamedId;
  column: NamedId;
  restorable: boolean;
}

/** One line for the overlay and the run-blocked list. Both surfaces say the
 *  same thing, so neither can drift from what the run door will do. */
export function cannotRunText(
  cannotRun: NonNullable<RecipeDetail["cannot_run"]>,
): string {
  const dead = cannotRun.dead_inputs ?? [];
  const first = dead[0];
  if (first) {
    const table = first.table.name ?? "its table";
    const named = dead
      .map((input) => input.column.name)
      .filter((name): name is string => Boolean(name));
    if (cannotRun.reason === "input_restorable" && named.length > 0) {
      return `${named.join(", ")} ${named.length === 1 ? "was" : "were"} deleted from ${table} — restore ${named.length === 1 ? "it" : "them"}, or edit the recipe.`;
    }
    return `${dead.length === 1 ? "a column" : `${dead.length} columns`} of ${table} ${dead.length === 1 ? "was" : "were"} permanently deleted — edit the recipe.`;
  }
  if (cannotRun.reason === "would_not_plan") {
    // The planner found it, so the planner says it — a second wording here
    // could only drift from what the run reports.
    return (
      cannotRun.detail ??
      "This recipe no longer runs against its tables as they are now."
    );
  }
  const params = cannotRun.params ?? [];
  return `${params.join(", ")} ${params.length === 1 ? "uses" : "use"} a category set the run cannot bind.`;
}

export interface MaterializeRequest {
  model_id: string;
  table_name: string;
  args?: Record<string, unknown>;
  picks?: Record<string, unknown>;
  lens?: RecipeLens;
  at?: { x: number; y: number };
  /** Reopened from a result: write into THIS table whatever the arguments,
   *  keeping its id, columns, position and links (R15 as amended). Absent on a
   *  drag from the panel, where a new configuration is a new table. */
  replace?: string;
}

export interface MaterializeResponse {
  table_id: string;
  replaced: boolean;
  rows: number;
  lens: string;
  read_ahead_of_release: string[];
}

/** Mirrors `ResultProvenance`. */
export interface ResultProvenance {
  table_id: string;
  model_id: string;
  recipe_id: string;
  version: number;
  recipe: {
    recipe_id: string;
    name: string;
    state: RecipeState;
    current_version: number;
  } | null;
  args: Record<string, unknown>;
  /** Every slot decided, in `picks` shape — sent back verbatim on a re-run. */
  picks: Record<string, unknown>;
  /** The same picks rendered for reading, ids resolved to current names by the
   *  server. Empty when the recipe is not this reader's to see. */
  picks_display: { label: string; value: string }[];
  lens: RecipeLens;
  ran_at: string;
  ran_by: RecipePerson;
  sources: {
    table_id: string;
    name: string | null;
    version: number;
    read_ahead_of_release: boolean;
  }[];
}

export const fetchRecipe = async (recipeId: string): Promise<RecipeDetail> => {
  const response = await axiosInstance.get<RecipeDetail>(
    `/api/recipes/${encodeURIComponent(recipeId)}`,
  );
  return response.data;
};

export const materializeRecipe = async (
  recipeId: string,
  body: MaterializeRequest,
): Promise<MaterializeResponse> => {
  const response = await axiosInstance.post<MaterializeResponse>(
    `/action/recipes/${encodeURIComponent(recipeId)}/materialize`,
    body,
  );
  return response.data;
};

/** 404 with `not_a_result` is the ordinary answer for a table nobody ran. */
export const fetchTableRecipe = async (
  tableId: string,
): Promise<ResultProvenance | null> => {
  try {
    const response = await axiosInstance.get<ResultProvenance>(
      `/api/tables/${encodeURIComponent(tableId)}/recipe`,
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null;
    throw err;
  }
};

/** The door's `{error, message}` body when there is one, else the transport's. */
export const describeRecipeError = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      { message?: string; error?: string } | undefined;
    return body?.message ?? body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : "The run failed.";
};

/**
 * The catalog page's listing, narrowed to ONE org — the active one. `q` swaps
 * the keyset page for a relevance-ranked search of the same org; both apply
 * R7's visibility in SQL, so nothing is filtered afterwards.
 */
export const listRecipes = async (params: {
  orgId: string;
  q?: string;
  after?: { name: string; recipe_id: string };
}): Promise<RecipeListPage> => {
  const { data } = await axiosInstance.get<RecipeListPage>("/api/recipes", {
    params: {
      org_id: params.orgId,
      ...(params.q ? { q: params.q } : {}),
      ...(params.after
        ? { after_name: params.after.name, after_id: params.after.recipe_id }
        : {}),
    },
  });
  return data;
};

/** Take a published recipe off the shelf, or put it back (R26). Reversible. */
export const setRecipeDeprecated = async (
  recipeId: string,
  deprecated: boolean,
): Promise<void> => {
  await axiosInstance.post(
    `/action/recipes/${encodeURIComponent(recipeId)}/deprecate`,
    { deprecated },
  );
};

/** Destroy a private recipe and every result it made. Not reversible (R26). */
export const deleteRecipe = async (recipeId: string): Promise<void> => {
  await axiosInstance.post(
    `/action/recipes/${encodeURIComponent(recipeId)}/delete`,
  );
};

/** What a dry run answers: the statement it proved, or why it would not run. */
export interface PlannedRecipe {
  runnable: boolean;
  /** Present when runnable — the applied statement for the picks that were
   *  sent. The client cannot build this: `recipe_core` has no WASM build. */
  sql?: string;
  lens?: RecipeLens;
  /** Present when not runnable — the planner's own words. */
  detail?: string;
}

/**
 * Plan a recipe at these picks and get the statement back.
 *
 * Two answers in one call: whether a run would be refused AT THESE PICKS
 * (rather than at the defaults, which is all the overlay's open-time check
 * knows), and the SQL that would run.
 */
export const planRecipe = async (
  recipeId: string,
  body: {
    args?: Record<string, unknown>;
    picks?: Record<string, unknown>;
    lens?: RecipeLens;
  },
): Promise<PlannedRecipe> => {
  const { data } = await axiosInstance.post<PlannedRecipe>(
    `/action/recipes/${encodeURIComponent(recipeId)}/plan`,
    body,
  );
  return data;
};
